import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import { useSiteLang as useLang } from '../siteLang';
import Footer from '../components/Footer';
import smiley from '../winston-smiley-white.svg';

// Refonte : on abandonne le chrome rétro Windows 95 (fenêtre beige, barre de
// titre dégradée, police MS Sans Serif) pour la nouvelle esthétique du site —
// Helvetica Neue épaisse, noir/blanc/crème uniquement, zéro arrondi, zéro
// ombre, boutons en texte nu façon .wlink.

const t = {
    fr: {
        windowTitle: 'winston.ie — Connexion',
        subtitle: 'Entre tes identifiants pour continuer.',
        username: 'Nom d\'utilisateur',
        password: 'Mot de passe',
        submit: 'Se connecter',
        loading: 'Connexion...',
        errNotFound: 'Nom d\'utilisateur introuvable.',
        errInvalid: 'Nom d\'utilisateur ou mot de passe invalide.',
        forgot: 'Mot de passe oublié ?',
        noAccount: 'Pas de compte ?',
        signup: 'S\'inscrire',
        backHome: '← winston.ie',
    },
    en: {
        windowTitle: 'winston.ie — Sign in',
        subtitle: 'Enter your credentials to continue.',
        username: 'Username',
        password: 'Password',
        submit: 'Sign in',
        loading: 'Signing in...',
        errNotFound: 'Username not found.',
        errInvalid: 'Invalid username or password.',
        forgot: 'Forgot password?',
        noAccount: 'No account?',
        signup: 'Sign up',
        backHome: '← winston.ie',
    },
    vi: {
        windowTitle: 'winston.ie — Đăng nhập',
        subtitle: 'Nhập thông tin đăng nhập của bạn để tiếp tục.',
        username: 'Tên người dùng',
        password: 'Mật khẩu',
        submit: 'Đăng nhập',
        loading: 'Đang đăng nhập...',
        errNotFound: 'Không tìm thấy tên người dùng.',
        errInvalid: 'Tên người dùng hoặc mật khẩu không hợp lệ.',
        forgot: 'Quên mật khẩu?',
        noAccount: 'Chưa có tài khoản?',
        signup: 'Đăng ký',
        backHome: '← winston.ie',
    },
};

const UI_FONT = "'Helvetica Neue', Arial, sans-serif";
const INK = '#0e1013';
const BG = '#faf9f6';
const BORDER = '#e5e5e0';
const MUTED = '#8a8a86';
const ERR_BG = '#fdf2f1';
const ERR_BORDER = '#e0a89f';
const ERR_TEXT = '#a3382f';

const fieldStyle = {
    width: '100%',
    border: `1px solid ${BORDER}`,
    padding: '11px 12px',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: UI_FONT,
    color: INK,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
};

function Login() {
    const navigate = useNavigate();
    const { lang, cycleLang } = useLang();
    const c = t[lang] || t.fr;
    const title = c.windowTitle.replace('winston.ie — ', '');

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => { document.title = 'Sign in | winston.ie'; }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const normalizedUsername = username.trim().toLowerCase();
        const { data: userRow, error: lookupError } = await supabase.from('users').select('email, role').eq('username', normalizedUsername).single();
        if (lookupError || !userRow) { setError(c.errNotFound); setLoading(false); return; }

        const { error: authError } = await supabase.auth.signInWithPassword({ email: userRow.email, password });
        if (authError) { setError(c.errInvalid); setLoading(false); return; }

        setLoading(false);
        navigate(userRow.role?.toLowerCase() === 'admin' ? '/choose-space' : '/admin/dashboard');
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: BG, fontFamily: UI_FONT }}>
            <style>{`
                .wlink { position: relative; display: inline-block; cursor: pointer; opacity: 0.88; transition: opacity .25s ease; }
                .wlink:hover { opacity: 1; }
                .wlink::after { content: ''; position: absolute; left: 0; right: 0; bottom: -3px; height: 1px; background: currentColor; transform: scaleX(0); transform-origin: left; transition: transform .35s cubic-bezier(.4,0,.2,1); }
                .wlink:hover::after { transform: scaleX(1); }
            `}</style>

            <div style={{ padding: '24px 28px' }}>
                <p onClick={() => navigate('/')} className="wlink" style={{ fontSize: '13px', fontWeight: 500, color: INK, margin: 0 }}>{c.backHome}</p>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div style={{ width: '100%', maxWidth: '380px', background: '#fff', border: `1px solid ${BORDER}`, padding: '40px 32px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
                        <img src={smiley} alt="winston.ie" style={{ height: '32px', width: 'auto', filter: 'invert(1)' }} />
                        <button onClick={cycleLang} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontFamily: UI_FONT, fontWeight: 500, fontSize: '12px', color: MUTED, cursor: 'pointer' }}>
                            {lang.toUpperCase()}
                        </button>
                    </div>

                    <h1 style={{ fontFamily: UI_FONT, fontWeight: 700, fontSize: '24px', color: INK, margin: '0 0 6px', letterSpacing: '-0.01em' }}>{title}</h1>
                    <p style={{ fontFamily: UI_FONT, fontWeight: 500, fontSize: '13px', color: MUTED, margin: '0 0 28px' }}>{c.subtitle}</p>

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>{c.username}</label>
                            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required style={fieldStyle} />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>{c.password}</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required style={fieldStyle} />
                        </div>

                        <p onClick={() => navigate('/forgot-password')} className="wlink" style={{ fontSize: '12px', fontWeight: 500, color: MUTED, textAlign: 'right', margin: '0 0 22px' }}>{c.forgot}</p>

                        {error && (
                            <div style={{ background: ERR_BG, border: `1px solid ${ERR_BORDER}`, padding: '10px 12px', fontSize: '12px', fontWeight: 500, color: ERR_TEXT, marginBottom: '18px' }}>
                                {error}
                            </div>
                        )}

                        <button type="submit" disabled={loading} className="wlink" style={{ display: 'block', width: '100%', textAlign: 'center', background: 'none', border: 'none', padding: '4px 0', fontFamily: UI_FONT, fontWeight: 700, fontSize: '15px', color: INK, cursor: 'pointer', opacity: loading ? 0.5 : undefined }}>
                            {loading ? c.loading : c.submit}
                        </button>
                    </form>

                    <p style={{ fontSize: '12px', fontWeight: 500, color: MUTED, textAlign: 'center', marginTop: '24px' }}>
                        {c.noAccount} <span onClick={() => navigate('/signup')} className="wlink" style={{ color: INK, fontWeight: 700 }}>{c.signup}</span>
                    </p>
                </div>
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default Login;